import { useState } from 'react'
import { X, ShoppingCart, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Spinner'
import { usePurchaseOrders, useUpdatePOStatus } from '@/hooks/useInventory'
import { useAuthStore } from '@/stores/authStore'
import type { PurchaseOrder, POStatus } from '@/types/app'

const STATUS_CONFIG: Record<
  POStatus,
  { label: string; badgeClasses: string }
> = {
  draft:      { label: 'Draft',      badgeClasses: 'bg-stone-100 text-stone-500' },
  submitted:  { label: 'Submitted',  badgeClasses: 'bg-blue-100 text-blue-700' },
  approved:   { label: 'Approved',   badgeClasses: 'bg-amber-100 text-amber-700' },
  rejected:   { label: 'Rejected',   badgeClasses: 'bg-red-100 text-red-700' },
  sent:       { label: 'Sent',       badgeClasses: 'bg-purple-100 text-purple-700' },
  delivered:  { label: 'Delivered',  badgeClasses: 'bg-green-100 text-green-700' },
  cancelled:  { label: 'Cancelled',  badgeClasses: 'bg-stone-100 text-stone-400' },
}

function fmt(amount: number | null): string {
  if (amount == null) return '—'
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function buildPoNumber(po: PurchaseOrder, index: number): string {
  const year = new Date(po.created_at).getFullYear()
  return `PO-${year}-${String(index + 1).padStart(4, '0')}`
}

function StatusBadge({ status }: { status: POStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeClasses}`}
    >
      {cfg.label}
    </span>
  )
}

interface WorkflowButtonsProps {
  po: PurchaseOrder
  onUpdate: (id: string, status: POStatus) => void
  loading: boolean
}

function WorkflowButtons({ po, onUpdate, loading }: WorkflowButtonsProps) {
  const role = useAuthStore(s => s.role)
  const isOwnerOrGm = role === 'owner' || role === 'gm'
  const canEdit = role === 'owner' || role === 'gm' || role === 'ops'

  if (po.status === 'draft' && canEdit) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onUpdate(po.id, 'submitted')}
        loading={loading}
      >
        Submit
      </Button>
    )
  }

  if (po.status === 'submitted' && isOwnerOrGm) {
    return (
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => onUpdate(po.id, 'approved')}
          loading={loading}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onUpdate(po.id, 'rejected')}
          loading={loading}
        >
          Reject
        </Button>
      </div>
    )
  }

  if (po.status === 'approved' && canEdit) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onUpdate(po.id, 'sent')}
        loading={loading}
      >
        Mark Sent
      </Button>
    )
  }

  if (po.status === 'sent' && canEdit) {
    return (
      <Button
        size="sm"
        onClick={() => onUpdate(po.id, 'delivered')}
        loading={loading}
      >
        Mark Delivered
      </Button>
    )
  }

  return null
}

function PODetailPanel({
  po,
  poNumber,
  onClose,
}: {
  po: PurchaseOrder
  poNumber: string
  onClose: () => void
}) {
  const updateStatus = useUpdatePOStatus()

  function handleUpdate(id: string, status: POStatus) {
    updateStatus.mutate({ id, status })
  }

  const lineTotal = (
    qty: number,
    unitPrice: number | null
  ): string => {
    if (unitPrice == null) return '—'
    return fmt(qty * unitPrice)
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-stone-900 font-mono">
              {poNumber}
            </h2>
            <StatusBadge status={po.status} />
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">
                Supplier
              </p>
              <p className="text-sm text-stone-800 font-medium">
                {po.supplier?.name ?? <span className="text-stone-400">—</span>}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">
                Total Amount
              </p>
              <p className="text-sm text-stone-800 font-semibold">{fmt(po.total_amount)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">
                Created
              </p>
              <p className="text-sm text-stone-600">{fmtDate(po.created_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">
                Submitted
              </p>
              <p className="text-sm text-stone-600">{fmtDate(po.submitted_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">
                Approved
              </p>
              <p className="text-sm text-stone-600">{fmtDate(po.approved_at)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-0.5">
                Delivered
              </p>
              <p className="text-sm text-stone-600">{fmtDate(po.delivered_at)}</p>
            </div>
          </div>

          {po.notes && (
            <div>
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-1">
                Notes
              </p>
              <p className="text-sm text-stone-600 whitespace-pre-wrap">{po.notes}</p>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-semibold text-stone-700">
              Items ({(po.items ?? []).length})
            </p>
            {(po.items ?? []).length === 0 ? (
              <p className="text-sm text-stone-400">No items on this PO.</p>
            ) : (
              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-50 border-b border-stone-200">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Ingredient
                      </th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Qty Ordered
                      </th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Qty Received
                      </th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Unit
                      </th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Unit Price
                      </th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(po.items ?? []).map(item => (
                      <tr
                        key={item.id}
                        className="border-b border-stone-100 last:border-0"
                      >
                        <td className="px-3 py-2.5 font-medium text-stone-800">
                          {item.ingredient?.name ?? (
                            <span className="text-stone-400 italic">Unknown</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right text-stone-700 tabular-nums">
                          {item.quantity_ordered}
                        </td>
                        <td className="px-3 py-2.5 text-right text-stone-700 tabular-nums">
                          {item.quantity_received ?? (
                            <span className="text-stone-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 text-xs font-medium">
                            {item.unit}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-stone-700 tabular-nums">
                          {item.unit_price != null ? fmt(item.unit_price) : (
                            <span className="text-stone-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold text-stone-800 tabular-nums">
                          {lineTotal(item.quantity_ordered, item.unit_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {po.total_amount != null && (
                    <tfoot>
                      <tr className="bg-stone-50 border-t border-stone-200">
                        <td
                          colSpan={5}
                          className="px-3 py-2 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide"
                        >
                          Total
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-stone-900 tabular-nums">
                          {fmt(po.total_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-stone-200">
          <WorkflowButtons
            po={po}
            onUpdate={handleUpdate}
            loading={updateStatus.isPending}
          />
        </div>
      </div>
    </>
  )
}

export function PurchaseOrdersPage() {
  const { data: pos, isLoading } = usePurchaseOrders()
  const updateStatus = useUpdatePOStatus()
  const [detailPO, setDetailPO] = useState<{ po: PurchaseOrder; index: number } | null>(null)

  if (isLoading) return <PageSpinner />

  const all = pos ?? []

  const counts = all.reduce<Record<string, number>>(
    (acc, po) => {
      acc[po.status] = (acc[po.status] ?? 0) + 1
      return acc
    },
    {}
  )

  function handleRowUpdate(id: string, status: POStatus) {
    updateStatus.mutate({ id, status })
  }

  return (
    <div className="p-6 space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Purchase Orders</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            Orders sent to ingredient suppliers
          </p>
        </div>
      </div>

      {all.length > 0 && (
        <div className="flex gap-4 flex-wrap">
          <span className="text-sm text-stone-500">
            <span className="font-semibold text-stone-800">{all.length}</span> total
          </span>
          {(
            [
              'draft',
              'submitted',
              'approved',
              'sent',
              'delivered',
            ] as POStatus[]
          )
            .filter(s => (counts[s] ?? 0) > 0)
            .map((s, i) => (
              <span key={s} className="flex items-center gap-4">
                {i > 0 && <span className="text-stone-400">·</span>}
                <span className="text-sm text-stone-500">
                  <span className="font-semibold text-stone-800">{counts[s]}</span>{' '}
                  {STATUS_CONFIG[s].label.toLowerCase()}
                </span>
              </span>
            ))}
        </div>
      )}

      {all.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <ShoppingCart size={32} className="text-stone-300 mb-3" />
          <p className="text-stone-600 font-medium">No purchase orders yet</p>
          <p className="text-sm text-stone-400 mt-1 max-w-sm">
            Create one from the Inventory page when items are low.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  PO #
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Supplier
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Created
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Items
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Total
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                  Actions
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {all.map((po, index) => {
                const poNumber = buildPoNumber(po, index)
                return (
                  <tr
                    key={po.id}
                    className="border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => setDetailPO({ po, index })}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-stone-700">
                        {poNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-stone-800">
                        {po.supplier?.name ?? (
                          <span className="text-stone-400">—</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {fmtDate(po.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="px-4 py-3 text-right text-stone-600 tabular-nums">
                      {(po.items ?? []).length}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-800 tabular-nums">
                      {fmt(po.total_amount)}
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={e => e.stopPropagation()}
                    >
                      <WorkflowButtons
                        po={po}
                        onUpdate={handleRowUpdate}
                        loading={updateStatus.isPending}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight size={15} className="text-stone-300" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailPO !== null && (
        <PODetailPanel
          po={detailPO.po}
          poNumber={buildPoNumber(detailPO.po, detailPO.index)}
          onClose={() => setDetailPO(null)}
        />
      )}
    </div>
  )
}
